// Identités & accès · Comptes staff (SA-3) — gérer les membres Orgazone : liste, fiche détail et TOUTES les
// actions réelles sur console_user (inviter, renvoyer, changer le rôle, désactiver/réactiver, retirer).
// ⚠️ DEUX identités : le RÔLE vit ici (Console) ; l'AUTHENTIFICATION (mot de passe/TOTP + e-mail) est déléguée
// à l'IdP. Trois onglets : les comptes STAFF (rôle Console), TOUTES les identités de l'IdP (staff + clients,
// avec leur 2FA) et le compte des caisses. ⚠️ Le JOURNAL D'AUDIT global n'est PLUS ici — il vit sur
// `/journal` (2026-08-12) : chercher « que s'est-il passé ? » n'est pas administrer des comptes. Seul
// l'audit D'UN compte reste, dans sa fiche, où il est une propriété de ce compte.
// ⚠️ §23.0 : tout vient de l'IdP réel ; ce qu'il ne sait pas —
// le rattachement d'un compte client à son entreprise — n'est pas affiché plutôt que deviné.
import { dateTimeLong } from '@orgazone/date-format';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useId, useState, type CSSProperties, type ReactNode } from 'react';
import { auditColor } from '../audit-format';
import { api, type StaffRow, type IdpAuditEvent, type IdpAccountInfo, type IdpAccountRow, type IdpGrant } from '../api';
import { EmptyState, useOverlayPanel } from '@orgazone/ui-web';
import { AdminAccountPanel, ChangeMobilePin, ChangerMotDePasse, useAdminAccount } from './AdminAccount';
import { useAuth, can } from '../auth';
import { alert, confirm, showToast } from '@orgazone/ui-web';
import { STAFF_ROLE, STAFF_ROLES, type StaffRole } from '../staff-roles';
import { AccountWizard } from './account-wizard';

// Scopes inter-outils suggérés (modules + délégation de support) — la saisie reste libre (convention <outil>:<rôle>[:<GLN>]).
const SCOPE_SUGGESTIONS = ['edi:admin', 'store:editor', 'ref:edit', 'bo:support:*', 'erp:support:*', 'store:support:*'];

type Role = StaffRole;

// Les rôles et ce qu'ils permettent : une seule définition, partagée avec l'assistant de création de compte.
const ROLES = STAFF_ROLES;
const ROLE = STAFF_ROLE;
const STATUS: Record<StaffRow['status'], { label: string; bg: string; fg: string }> = {
  invited: { label: 'Invité', bg: 'var(--wa-bg)', fg: 'var(--wa-fg)' },
  active: { label: 'Actif', bg: 'var(--ok-bg)', fg: 'var(--ok-fg)' },
  disabled: { label: 'Désactivé', bg: 'var(--ne-bg)', fg: 'var(--ne-fg)' },
};

const fmtDate = dateTimeLong; // « 4 août 2026 à 14:32 » — tiret si absent

import { Pill as UiPill, initials } from '../ui';
import { TableScroll } from '../table-scroll';
// Pastille de ligne — géométrie « row » (cf. ../ui).
const Pill = ({ bg, fg, children }: { bg: string; fg: string; children: ReactNode }) => <UiPill bg={bg} fg={fg} shape="row">{children}</UiPill>;
const chip: CSSProperties = { fontSize: 'var(--t-sub)', padding: '2px 8px', borderRadius: 'var(--r-xs)', whiteSpace: 'nowrap' };
const fieldSt: CSSProperties = { width: '100%', padding: '9px 12px', border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-control)' };

function Avatar({ name, size = 42 }: { name: string; size?: number }) {
  return <div style={{ width: size, height: size, borderRadius: size / 3.4, background: 'var(--ink100)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
    <span className="mono" style={{ fontSize: size / 3.2, color: 'var(--ink600)' }}>{initials(name)}</span></div>;
}

// ── Modale : changer le rôle d'un compte ─────────────────────────────────────────────────────────
function ChangeRole({ staff, onClose, onDone }: { staff: StaffRow; onClose: () => void; onDone: (notice: string) => void }) {
  const [role, setRole] = useState<Role>(staff.role);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  const { panelRef } = useOverlayPanel({ open: true, onClose });
  const titleId = useId();

  const submit = async () => {
    if (role === staff.role) { onClose(); return; }
    setErr(null); setBusy(true);
    try { await api.updateStaff(staff.id, { role }); onDone(`Rôle de ${staff.name} : ${ROLE[role].label}.`); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="modalwrap" onClick={onClose}>
      <div className="modal" ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}>
        <h3 id={titleId}>Rôle de {staff.name}</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
          {ROLES.map((r) => {
            const on = r.v === role;
            return (
              <button aria-pressed={on} key={r.v} type="button" onClick={() => setRole(r.v)}
                className={on ? 'row-pick selected' : 'row-pick'}
                style={{ textAlign: 'left', padding: '11px 13px', color: 'var(--ink900)' }}>
                <div style={{ fontSize: 'var(--t-title)', fontWeight: 700 }}>{r.label}</div>
                <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 3 }}>{r.hint}</div>
              </button>
            );
          })}
        </div>
        {err && <div className="err">{err}</div>}
        <div className="modalfoot">
          <button type="button" className="ghost btn-ghost" onClick={onClose}>Annuler</button>
          <button type="button" className="btn btn-cta" disabled={busy} onClick={() => void submit()}>{busy ? '…' : 'Enregistrer le rôle'}</button>
        </div>
      </div>
    </div>
  );
}

// ── TOUTES LES IDENTITÉS (maquette : les 312 comptes de l'IdP, realms staff ET clients) — la Console
// ne montrait que le personnel qu'elle a créé, donc un compte client n'existait nulle part dans le plan
// de contrôle. Colonnes de la maquette : type de compte, 2FA, statut, dernière connexion.
// ⚠️ Le RATTACHEMENT (à quel client appartient un compte) n'est pas connu de l'IdP : il vient de
// `tenant_account` (§26.4quater), joint par le serveur. On ne le déduit PAS des grants — un compte
// fraîchement invité n'a encore aucun accès, donc aucun grant, et paraîtrait sans client alors qu'il
// en a un. Un compte client sans rattachement est une ANOMALIE, et l'écran la nomme.
function AllAccountsTab() {
  const [accounts, setAccounts] = useState<IdpAccountRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [realm, setRealm] = useState<'' | 'staff' | 'clients'>('');
  const [noTwofa, setNoTwofa] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => {
    api.identityAccounts().then((r) => { setAccounts(r.accounts); setErr(null); }).catch((e) => setErr((e as Error).message));
  }, []);

  if (err) return <div className="dcard" style={{ padding: 18 }}><div className="err">Comptes IdP indisponibles : {err}</div></div>;
  if (!accounts) return <div className="dcard" style={{ padding: 18 }}><span className="muted">Chargement des identités…</span></div>;

  const list = accounts
    .filter((a) => !realm || a.realm === realm)
    .filter((a) => !noTwofa || !a.twofa)
    .filter((a) => !q.trim() || `${a.email} ${a.name ?? ''} ${a.tenantName ?? ''}`.toLowerCase().includes(q.trim().toLowerCase()));
  const seg = (on: boolean) => ({ padding: '5px 11px', borderRadius: 'var(--r-xs)', border: 0, cursor: 'pointer', fontSize: 'var(--t-dense)', fontWeight: 600, background: on ? 'var(--canvas)' : 'transparent', color: on ? 'var(--ink900)' : 'var(--ink500)' } as const);

  return (
    <div className="dcard" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--ink200)', background: 'var(--head)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 2, padding: 3, border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', background: 'var(--ink100)' }}>
          {([['', 'Tous'], ['staff', 'Orgazone'], ['clients', 'Clients']] as const).map(([k, label]) => (
            <button aria-pressed={realm === k} key={k} onClick={() => setRealm(k)} style={seg(realm === k)}>
              {label}<span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginLeft: 6 }}>{k ? accounts.filter((a) => a.realm === k).length : accounts.length}</span>
            </button>
          ))}
        </div>
        {/* Bascule maquette : voir d'un geste qui n'a pas de second facteur. */}
        <button aria-pressed={noTwofa} onClick={() => setNoTwofa((v) => !v)}
          style={{ height: 28, padding: '0 11px', borderRadius: 'var(--r-xs)', cursor: 'pointer', fontSize: 'var(--t-note)', fontWeight: 600, border: `1px solid ${noTwofa ? 'var(--wa-fg)' : 'var(--ink200)'}`, background: noTwofa ? 'var(--wa-bg)' : 'transparent', color: noTwofa ? 'var(--wa-fg)' : 'var(--ink600)' }}>
          2FA absente uniquement
        </button>
        <div style={{ flex: 1 }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e-mail, nom, client…"
          style={{ width: 200, height: 28, padding: '0 10px', borderRadius: 'var(--r-xs)', border: '1px solid var(--ink200)', background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-dense)', outline: 'none' }} />
        <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{list.length} / {accounts.length}</span>
      </div>
      {list.length === 0 ? (
        <EmptyState compact icon="users" title="Aucun compte" note="Aucune identité ne correspond à ce filtre." />
      ) : (
        <TableScroll minWidth={720}>
            <thead><tr>
              {['Compte', 'Type', 'Client', '2FA', 'Statut', 'Dernière connexion'].map((h) => (
                <th key={h} style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.07em', background: 'var(--ink-50)', padding: '7px 12px' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {list.map((a) => (
                <tr key={a.email} style={{ borderTop: '1px solid var(--ink100)' }}>
                  <td style={{ padding: '8px 12px' }}>
                    <div style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>{a.name ?? a.email.split('@')[0]}</div>
                    <div className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{a.email}</div>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Pill bg={a.realm === 'staff' ? 'var(--vi-bg)' : 'var(--in-bg)'} fg={a.realm === 'staff' ? 'var(--vi-fg)' : 'var(--in-fg)'}>
                      {a.realm === 'staff' ? 'Orgazone' : 'Client'}
                    </Pill>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    {a.realm === 'staff'
                      ? <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink400)' }}>—</span>
                      : a.tenantName
                        ? <span style={{ fontSize: 'var(--t-dense)' }}>{a.tenantName}</span>
                        /* ⚠️ Un compte client sans rattachement n'ouvre rien et n'apparaît sur aucune fiche :
                           il se DIT, il ne se laisse pas en case vide (§23.0). */
                        : <span style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: 'var(--wa-fg)' }}>non rattaché</span>}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    {a.twofa
                      ? <span style={{ fontSize: 'var(--t-meta)', fontWeight: 700, color: 'var(--ok-fg)' }}>TOTP ✓</span>
                      : <span style={{ fontSize: 'var(--t-meta)', fontWeight: 700, color: 'var(--wa-fg)' }}>absente</span>}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Pill bg={STATUS[a.status].bg} fg={STATUS[a.status].fg}>{STATUS[a.status].label}</Pill>
                  </td>
                  <td className="mono" style={{ padding: '8px 12px', fontSize: 'var(--t-sub)', color: 'var(--ink500)', whiteSpace: 'nowrap' }}>
                    {a.lastLoginAt ? fmtDate(a.lastLoginAt) : 'jamais'}
                  </td>
                </tr>
              ))}
            </tbody>
        </TableScroll>
      )}
      <div style={{ padding: '9px 16px', borderTop: '1px solid var(--ink200)', fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>
        Vue de l'IdP. Le <b>rôle Console</b> ne concerne que le personnel Orgazone (onglet Comptes) ; les accès des comptes clients sont des <b>grants</b>, visibles sur leur fiche.
      </div>
    </div>
  );
}

export function Staff() {
  const [rows, setRows] = useState<StaffRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [meId, setMeId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // ⚠️ Même contrat que la fiche client : l'accès rapide du tableau de bord OUVRE la modale. Le
  // paramètre est consommé — sans quoi elle se rouvrirait au retour arrière et au rechargement.
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get('nouveau') !== '1') return;
    setCreating(true);
    const reste = new URLSearchParams(params);
    reste.delete('nouveau');
    setParams(reste, { replace: true });
  }, [params, setParams]);
  const [editingRole, setEditingRole] = useState<StaffRow | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | StaffRow['status']>('all');
  const [q, setQ] = useState('');
  // Onglets de l'écran Identités & accès (maquette).
  // ⚠️ TROIS onglets, et le dernier n'est PAS de la même nature que les deux autres — c'est
  // délibéré. « Comptes staff » et « Toutes les identités » listent des PERSONNES (une identité, un
  // e-mail, une session, un rôle). « Compte des caisses » est UN secret partagé, sans personne
  // derrière, répliqué sur tout le parc. Les réunir dans une même liste ferait courir le risque exact
  // qu'on cherche à éviter : « retirer un utilisateur » et verrouiller toutes les tablettes sans l'avoir
  // voulu. Même écran pour les trouver, tables séparées pour ne pas les confondre.
  const [tab, setTab] = useState<'comptes' | 'identites' | 'caisses'>('comptes');
  // Compte des caisses : l'état est lu ICI (et pas dans le panneau) parce que l'EN-TÊTE en a besoin —
  // le libellé du bouton dépend de son existence (« Poser » vs « Changer »).
  const { user } = useAuth();
  const compteCaisses = useAdminAccount(tab === 'caisses');
  const [mdpOuvert, setMdpOuvert] = useState(false);
  const [mobilePinOpen, setMobilePinOpen] = useState(false);

  const load = async () => { try { const { staff } = await api.staff(); setRows(staff); } catch (e) { setErr((e as Error).message); } };
  // ⚠️ Si `me()` échoue, on ne sait PAS qui est connecté → meId reste null et `isSelf` doit être traité
  // en FAIL-CLOSED (cf. calcul plus bas) : un catch muet qui laisse isSelf=false désarmerait la garde
  // anti-auto-verrouillage (l'utilisateur pourrait se désactiver lui-même).
  useEffect(() => { void load(); void api.me().then((r) => setMeId(r.user.id)).catch(() => setMeId(null)); }, []);

  const selected = rows?.find((r) => r.id === selectedId) ?? null;
  // Un compte disparu (retiré) referme la fiche automatiquement.
  useEffect(() => { if (selectedId && rows && !selected) setSelectedId(null); }, [rows, selectedId, selected]);

  const act = async (id: string, fn: () => Promise<string>) => {
    setBusyId(id);
    try { showToast(await fn()); await load(); }
    catch (e) { void alert({ title: 'Action impossible', message: (e as Error).message }); }
    finally { setBusyId(null); }
  };
  const resend = (s: StaffRow) => act(s.id, async () => {
    const r = await api.resendInvite(s.id);
    return r.alreadyActive ? `${s.email} a déjà un accès IdP actif.` : r.inviteSent ? `Invitation renvoyée à ${s.email}.` : `Envoi impossible : e-mail de l'IdP non configuré.`;
  });
  const toggleStatus = (s: StaffRow) => act(s.id, async () => {
    const next = s.status === 'disabled' ? 'active' : 'disabled';
    await api.updateStaff(s.id, { status: next });
    return next === 'disabled' ? `${s.name} désactivé.` : `${s.name} réactivé.`;
  });
  const remove = async (s: StaffRow) => {
    const ok = await confirm({ title: `Retirer ${s.name} ?`, message: `Son rôle Console est supprimé (l'identité IdP subsiste). Cette action est irréversible.`, confirmLabel: 'Retirer' });
    if (!ok) return;
    await act(s.id, async () => { await api.deleteStaff(s.id); return `${s.name} retiré des comptes staff.`; });
  };

  if (err) return <div className="page"><div className="err">Erreur : {err}</div></div>;
  if (!rows) return <div className="page"><div className="muted">Chargement des comptes…</div></div>;

  const counts = { all: rows.length, active: 0, invited: 0, disabled: 0 } as Record<'all' | StaffRow['status'], number>;
  rows.forEach((r) => { counts[r.status] += 1; });
  const list = rows
    .filter((r) => filter === 'all' || r.status === filter)
    .filter((r) => !q.trim() || (r.name + ' ' + r.email).toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div className="page">
      {creating && <AccountWizard onClose={() => setCreating(false)} onDone={(n) => { setCreating(false); showToast(n); void load(); }} />}
      {editingRole && <ChangeRole staff={editingRole} onClose={() => setEditingRole(null)} onDone={(n) => { setEditingRole(null); showToast(n); void load(); }} />}
      {/* Les modales de l'écran sont montées ICI, toutes ensemble — comme leurs déclencheurs le sont
          dans l'en-tête. Une modale déclarée au fil du contenu se retrouve, tôt ou tard, ouverte depuis
          un endroit différent des autres. */}
      {mdpOuvert && (
        <ChangerMotDePasse
          existe={!!compteCaisses.etat?.exists}
          libelleActuel={compteCaisses.etat?.label ?? 'ADMIN'}
          onClose={() => setMdpOuvert(false)}
          onDone={(n, tone) => { setMdpOuvert(false); showToast(n, tone); compteCaisses.reload(); }}
        />
      )}
      {mobilePinOpen && (
        <ChangeMobilePin
          exists={!!compteCaisses.etat?.mobilePin?.exists}
          onClose={() => setMobilePinOpen(false)}
          onDone={(n, tone) => { setMobilePinOpen(false); showToast(n, tone); compteCaisses.reload(); }}
        />
      )}

      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">Utilisateurs</h1>
          <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink500)', marginTop: 3 }}>Le personnel Orgazone (realm STAFF) et le compte d&apos;administration des caisses — deux natures d&apos;accès, deux tableaux.</div>
        </div>
        <div style={{ flex: 1 }} />
        {tab === 'comptes' && <button className="btn btn-cta sm" onClick={() => { setCreating(true); }}>+ Inviter un compte</button>}
        {/* ⚠️ LE COMPTE D'ADMINISTRATION N'A PAS DE BOUTON ICI : chaque secret a SA tuile, et le geste qui le
            change vit à côté de ce qu'il change. Deux boutons d'en-tête pour deux secrets obligeaient à lire
            leur libellé pour savoir lequel on touchait — le geste le plus grave de l'écran. Les modales, elles,
            restent montées ci-dessus, avec les autres. */}
      </div>

      {/* ⚠️ Plus de « Journal d'audit » ici : la maquette l'avait logé dans cet écran, or chercher
          « que s'est-il passé ? » n'est pas administrer des comptes. Il vit sur /journal (décision
          utilisateur 2026-08-12). L'audit D'UN compte reste dans sa fiche : là, c'est sa propriété. */}
      <div className="tabs" style={{ marginBottom: 16 }}>
        {([['comptes', `Comptes staff (${rows.length})`], ['identites', 'Toutes les identités'], ['caisses', 'Compte d’administration']] as const).map(([k, label]) => (
          <button key={k} onClick={() => { setTab(k); setSelectedId(null); }}
            className={tab === k ? 'tab active' : 'tab'}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'caisses' ? (
        <AdminAccountPanel
          {...compteCaisses}
          canWrite={can(user, 'console.adminaccount.write')}
          onChangePassword={() => setMdpOuvert(true)}
          onChangeMobilePin={() => setMobilePinOpen(true)}
        />
      ) : tab === 'identites' ? <AllAccountsTab /> : selected ? (
        /* ══ FICHE DÉTAIL ══ */
        <StaffDetail
          s={selected} isSelf={meId === null ? true : selected.id === meId} busy={busyId === selected.id}
          onBack={() => setSelectedId(null)}
          onResend={() => void resend(selected)}
          onChangeRole={() => setEditingRole(selected)}
          onToggle={() => void toggleStatus(selected)}
          onRemove={() => void remove(selected)}
        />
      ) : (
        /* ══ LISTE ══ */
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 2, padding: 3, border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', background: 'var(--ink100)' }}>
              {(['all', 'active', 'invited', 'disabled'] as const).map((f) => (
                <button aria-pressed={filter === f} key={f} onClick={() => setFilter(f)}
                  style={{ padding: '5px 11px', borderRadius: 'var(--r-xs)', border: 0, cursor: 'pointer', fontSize: 'var(--t-dense)', fontWeight: 600,
                    background: filter === f ? 'var(--canvas)' : 'transparent', color: filter === f ? 'var(--ink900)' : 'var(--ink500)' }}>
                  {f === 'all' ? 'Tous' : STATUS[f].label}<span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginLeft: 6 }}>{counts[f]}</span>
                </button>
              ))}
            </div>
            <div style={{ flex: 1 }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, e-mail…" style={{ ...fieldSt, width: 220, padding: '7px 12px', fontSize: 'var(--t-lead)' }} />
          </div>

          <div className="dcard" style={{ overflow: 'hidden' }}>
            {list.length === 0 ? (
              <EmptyState icon="users" title="Aucun compte pour ce filtre" note="Changez de filtre, ou invitez un membre avec « + Ajouter un membre » (compte créé dans l’IdP, invitation envoyée)." />
            ) : (
              <TableScroll minWidth={560}>
                <thead><tr><th>Membre</th><th>Rôle</th><th>Statut</th><th>Dernière connexion</th><th></th></tr></thead>
                <tbody>
                  {list.map((s) => (
                    <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => { setSelectedId(s.id); }}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Avatar name={s.name} size={32} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600 }}>{s.name}</div>
                            <div className="mono" style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)' }}>{s.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>{ROLE[s.role]?.label ?? s.role}</td>
                      <td><Pill bg={STATUS[s.status].bg} fg={STATUS[s.status].fg}>{STATUS[s.status].label}</Pill></td>
                      <td className="mono" style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)' }}>{fmtDate(s.lastLoginAt)}</td>
                      {/* ⚠️ LE CHEVRON EST LE BOUTON DE LA LIGNE — le chemin du clavier ; le clic sur la ligne n'en est qu'un raccourci. */}
                      <td style={{ textAlign: 'right' }}>
                        <button type="button" aria-label={`Ouvrir la fiche de ${s.name}`} onClick={(e) => { e.stopPropagation(); setSelectedId(s.id); }}
                          style={{ border: 'none', background: 'none', padding: '2px 6px', font: 'inherit', color: 'var(--ink400)', cursor: 'pointer', borderRadius: 'var(--r-xs)' }}>›</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TableScroll>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Fiche d'un compte : identité + rôle/permissions (réels) + actions (réelles) + sections IdP « à venir » honnêtes.
function StaffDetail({ s, isSelf, busy, onBack, onResend, onChangeRole, onToggle, onRemove }: {
  s: StaffRow; isSelf: boolean; busy: boolean;
  onBack: () => void; onResend: () => void; onChangeRole: () => void; onToggle: () => void; onRemove: () => void;
}) {
  // Sécurité + journal + grants viennent de l'IdP (identité) : chargés à l'ouverture, remis à zéro par compte.
  const [acct, setAcct] = useState<IdpAccountInfo | null>(null);
  const [audit, setAudit] = useState<IdpAuditEvent[] | null>(null);
  const [grants, setGrants] = useState<IdpGrant[] | null>(null);
  const [idpErr, setIdpErr] = useState<string | null>(null);
  const [scopeInput, setScopeInput] = useState('');
  const [bounded, setBounded] = useState(false); // délégation bornée 8 h (support)
  const [grantBusy, setGrantBusy] = useState(false);
  const [grantErr, setGrantErr] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    setAcct(null); setAudit(null); setGrants(null); setIdpErr(null); setGrantErr(null);
    Promise.all([api.identityAccount(s.email), api.identityAudit(s.email), api.identityGrants(s.email)])
      .then(([a, j, g]) => { if (alive) { setAcct(a); setAudit(j.events); setGrants(g.grants); } })
      .catch((e) => { if (alive) setIdpErr((e as Error).message); });
    return () => { alive = false; };
  }, [s.email]);
  // ⚠️ L'échec du rechargement se DIT : sinon la liste d'accès restait silencieusement périmée après
  // un « Accorder »/« Révoquer », donnant à croire que rien n'avait bougé (§23.0).
  const reloadGrants = () => api.identityGrants(s.email)
    .then((g) => { setGrants(g.grants); setGrantErr(null); })
    .catch((e) => setGrantErr(`Accès non rechargés : ${(e as Error).message}`));
  const doGrant = async () => {
    const scope = scopeInput.trim();
    if (!scope) return;
    setGrantErr(null); setGrantBusy(true);
    try {
      const expiresAt = bounded ? new Date(Date.now() + 8 * 3600 * 1000).toISOString() : undefined;
      await api.grantScope({ email: s.email, scope, expiresAt });
      setScopeInput(''); setBounded(false); await reloadGrants();
    } catch (e) { setGrantErr((e as Error).message); }
    finally { setGrantBusy(false); }
  };
  const doRevoke = async (id: string) => { setGrantErr(null); try { await api.revokeGrant(id); await reloadGrants(); } catch (e) { setGrantErr((e as Error).message); } };
  const r = ROLE[s.role];
  const st = STATUS[s.status];
  const facts: { k: string; v: string }[] = [
    { k: 'Rôle', v: r?.label ?? s.role },
    { k: 'Statut', v: st.label },
    { k: 'Invité par', v: s.invitedByName ?? '— (compte initial)' },
    { k: 'Créé le', v: fmtDate(s.createdAt) },
    { k: 'Dernière connexion', v: fmtDate(s.lastLoginAt) },
  ];
  const actionBtn: CSSProperties = { display: 'flex', alignItems: 'center', width: '100%', padding: '9px 12px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink200)', background: 'transparent', color: 'var(--ink900)', fontSize: 'var(--t-body)', fontWeight: 600, cursor: 'pointer' };

  return (
    <>
      <button onClick={onBack} className="ghost btn-ghost" style={{ width: 'auto', padding: '5px 11px', marginBottom: 12 }}>← Tous les comptes</button>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 14, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Identité */}
          <div className="dcard" style={{ padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Avatar name={s.name} />
              <div style={{ minWidth: 0 }}>
                <h1 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{s.name}</h1>
                <div className="mono" style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)' }}>{s.email}</div>
              </div>
              <div style={{ flex: 1 }} />
              <Pill bg={st.bg} fg={st.fg}>{st.label}</Pill>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: '14px 20px', marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--ink100)' }}>
              {facts.map((f) => (
                <div key={f.k}>
                  <div className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink500)' }}>{f.k}</div>
                  <div style={{ fontSize: 'var(--t-lead)', color: 'var(--ink700)', marginTop: 3 }}>{f.v}</div>
                </div>
              ))}
            </div>
            {/* Sécurité IdP (réel) : realm + 2FA — remplace l'ancien bloc « 2FA à venir ». */}
            <div style={{ marginTop: 12, fontSize: 'var(--t-dense)', color: 'var(--ink500)' }}>
              {idpErr ? <span style={{ color: 'var(--wa-fg)' }}>Sécurité IdP indisponible : {idpErr}</span>
                : !acct ? 'Sécurité IdP : chargement…'
                : !acct.exists ? <span style={{ color: 'var(--wa-fg)' }}>Aucune identité IdP pour cet e-mail — à (re)provisionner.</span>
                : <>Sécurité IdP — realm <b style={{ color: 'var(--ink700)' }}>{acct.realm}</b> · 2FA {acct.twofa
                    ? <b style={{ color: 'var(--ok-fg)' }}>activée</b>
                    : <b style={{ color: 'var(--wa-fg)' }}>en attente d'enrôlement</b>}</>}
            </div>
          </div>

          {/* Rôle & permissions (réel) */}
          <div className="dcard" style={{ padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <span style={{ fontSize: 'var(--t-title)', fontWeight: 700 }}>Rôle &amp; permissions</span>
              <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{r?.label}</span>
            </div>
            <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)', marginBottom: 10 }}>{r?.hint}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {r?.can.map((c) => <span key={c} style={{ ...chip, fontSize: 'var(--t-meta)', background: 'var(--ok-bg)', color: 'var(--ok-fg)' }}>{c}</span>)}
              {r?.cannot.map((c) => <span key={c} style={{ ...chip, fontSize: 'var(--t-meta)', background: 'var(--ink100)', color: 'var(--ink500)', textDecoration: 'line-through' }}>{c}</span>)}
            </div>
            {/* Matrice d'accès — GRANTS RBAC RÉELS (idp_grant, émis dans le token). Accorder/révoquer = super-admin. */}
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--ink100)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 'var(--t-body)', fontWeight: 700 }}>Accès inter-outils</span>
                <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)' }}>&lt;outil&gt;:&lt;rôle&gt;[:&lt;GLN&gt;]</span>
              </div>
              {idpErr ? <div className="avenir">Grants IdP indisponibles : {idpErr}</div>
                : grants === null ? <div className="muted" style={{ fontSize: 'var(--t-dense)' }}>Chargement…</div>
                : grants.length === 0 ? <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)' }}>Aucun accès inter-outils accordé.</div>
                : <div>
                    {grants.map((g) => {
                      const expired = !!g.expiresAt && new Date(g.expiresAt) < new Date();
                      return (
                        <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--ink100)' }}>
                          <span className="mono" style={{ fontSize: 'var(--t-dense)', fontWeight: 600, color: expired ? 'var(--ink400)' : 'var(--ink700)', textDecoration: expired ? 'line-through' : 'none' }}>{g.scope}</span>
                          <span style={{ fontSize: 'var(--t-sub)', color: expired ? 'var(--da-fg)' : 'var(--ink500)' }}>{g.expiresAt ? (expired ? 'expiré' : `expire ${fmtDate(g.expiresAt)}`) : 'permanent'}</span>
                          <div style={{ flex: 1 }} />
                          <button className="ghost btn-ghost" style={{ width: 'auto', padding: '2px 9px', fontSize: 'var(--t-meta)', color: 'var(--da-fg)' }} onClick={() => void doRevoke(g.id)}>Révoquer</button>
                        </div>
                      );
                    })}
                  </div>}
              {/* Accorder un accès */}
              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <input list="scope-suggestions" value={scopeInput} onChange={(e) => setScopeInput(e.target.value)} placeholder="edi:admin, store:editor…" className="mono"
                  style={{ flex: '1 1 150px', minWidth: 0, padding: '6px 10px', border: '1px solid var(--ink200)', borderRadius: 'var(--r-xs)', background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-dense)' }} />
                <datalist id="scope-suggestions">{SCOPE_SUGGESTIONS.map((sc) => <option key={sc} value={sc} />)}</datalist>
                <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 'var(--t-note)', color: 'var(--ink500)' }} title="Délégation de support bornée à 8 h (§24.9quinquies)">
                  <input type="checkbox" checked={bounded} onChange={(e) => setBounded(e.target.checked)} /> borné 8 h
                </label>
                <button className="btn btn-cta sm" disabled={grantBusy || !scopeInput.trim()} onClick={() => void doGrant()}>{grantBusy ? '…' : 'Accorder'}</button>
              </div>
              {grantErr && <div className="err">{grantErr}</div>}
            </div>
          </div>

          {/* Journal du compte — RÉEL (idp_audit, filtré sur cet e-mail). */}
          <div className="dcard" style={{ padding: 18 }}>
            <div style={{ fontSize: 'var(--t-title)', fontWeight: 700, marginBottom: 10 }}>
              Journal du compte <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', fontWeight: 400 }}>idp_audit</span>
            </div>
            {idpErr ? <div className="avenir">Journal IdP indisponible : {idpErr}</div>
              : audit === null ? <div className="muted" style={{ fontSize: 'var(--t-body)' }}>Chargement…</div>
              : audit.length === 0 ? <EmptyState compact icon="history" title="Aucun événement" note="Les connexions et actions IdP de ce compte s’afficheront ici." />
              : <div>
                  {audit.map((e) => (
                    <div key={e.id} style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '7px 0', borderBottom: '1px solid var(--ink100)' }}>
                      <span className="mono" style={{ fontSize: 'var(--t-meta)', fontWeight: 600, color: auditColor(e.action), minWidth: 148 }}>{e.action}</span>
                      <span className="mono" style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-meta)', color: 'var(--ink500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.ip ?? '—'}</span>
                      <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink400)', whiteSpace: 'nowrap' }}>{fmtDate(e.at)}</span>
                    </div>
                  ))}
                </div>}
          </div>
        </div>

        {/* Actions (réelles) */}
        <div className="dcard" style={{ padding: '14px 16px' }}>
          <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700, marginBottom: 10 }}>Actions</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {s.status === 'invited' && (
              <button style={actionBtn} disabled={busy} onClick={onResend}>
                <span style={{ flex: 1, textAlign: 'left' }}>Renvoyer l'invitation</span>
                <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink400)' }}>e-mail</span>
              </button>
            )}
            <button style={actionBtn} disabled={busy} onClick={onChangeRole}>
              <span style={{ flex: 1, textAlign: 'left' }}>Changer le rôle</span>
              <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink400)' }}>{ROLE[s.role]?.label}</span>
            </button>
            {!isSelf && (
              <button style={actionBtn} disabled={busy} onClick={onToggle}>
                <span style={{ flex: 1, textAlign: 'left' }}>{s.status === 'disabled' ? 'Réactiver le compte' : 'Désactiver le compte'}</span>
                <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink400)' }}>{s.status === 'disabled' ? 'actif' : 'coupe l’accès'}</span>
              </button>
            )}
            {!isSelf && (
              <button style={{ ...actionBtn, color: 'var(--da-fg)', borderColor: 'var(--da-bg)' }} disabled={busy} onClick={onRemove}>
                <span style={{ flex: 1, textAlign: 'left' }}>Retirer le compte</span>
                <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--da-fg)' }}>supprime le rôle</span>
              </button>
            )}
          </div>
          <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 11, lineHeight: 1.5 }}>
            {isSelf ? 'C’est votre compte : désactivation et retrait sont désactivés (anti-verrouillage).'
              : 'Aucun mot de passe ici : l’utilisateur le pose via son lien d’invitation ou de réinitialisation (IdP).'}
          </div>
        </div>
      </div>
    </>
  );
}
